<script>
import { vehicleLabel } from '@/Modules/vehicleLabel.js';
import { router } from '@inertiajs/vue3';
import Button from 'primevue/button';
import DataView from 'primevue/dataview';
import Dialog from 'primevue/dialog';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';
import { commandPaletteCommands } from '@/ShopManagement/commandPaletteCommands.js';

export default {
  components: {
    Button,
    DataView,
    Dialog,
    IconField,
    InputIcon,
    InputText,
    Message,
    Skeleton,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedScope: {
      type: Object,
      default: null,
    },
    showEstimate: {
      type: Boolean,
      default: false,
    },
    pageSections: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['close', 'create-estimate', 'jump-page-section'],

  data() {
    return {
      query: '',
      masterConfig: this.$page.props.master_config,
      apiSearchResults: [],
      currentSearchPage: 1,
      hasMoreSearchResults: false,
      isSearching: false,
      isLoadingMoreResults: false,
      searchDebounceTimer: null,
      searchError: null,
      failedSearch: null,
      latestSearchRequestId: 0,
      searchResultsPerPage: 8,
      searchSkeletonRows: [
        { titleWidth: '62%', subtitleWidth: '38%' },
        { titleWidth: '48%', subtitleWidth: '32%' },
        { titleWidth: '70%', subtitleWidth: '44%' },
      ],
    };
  },

  computed: {
    normalizedQuery() {
      return this.query.trim().toLowerCase();
    },

    scopeLabel() {
      return this.selectedScope?.name || 'MSO Manager';
    },

    resultsLabel() {
      if (this.isSearching && this.visibleResults.length === 0) {
        return 'Searching';
      }

      if (!this.normalizedQuery) {
        return this.pageSectionResults.length
          ? 'Page sections and suggested actions'
          : 'Suggested actions';
      }

      const count = this.visibleResults.length;

      return count === 1 ? '1 result' : `${count} results`;
    },

    primaryCommands() {
      return this.commandResults.filter((command) => command.primary);
    },

    commandResults() {
      return commandPaletteCommands
        .filter((command) => this.showEstimate || command.action !== 'create-estimate')
        .map((command) => ({
          ...command,
          type: 'Command',
          searchableText: [command.slash, command.title, command.subtitle, ...command.keywords]
            .join(' ')
            .toLowerCase(),
        }));
    },

    pageSectionResults() {
      return this.pageSections
        .filter((section) => section?.id && section?.label)
        .map((section) => ({
          id: `page-section-${section.id}`,
          type: 'Page section',
          title: section.label,
          subtitle: 'Jump to this section',
          icon: 'fa-solid fa-arrow-turn-down',
          section,
          isPageSection: true,
          searchableText: [section.label, 'page section jump to'].join(' ').toLowerCase(),
        }));
    },

    visibleResults() {
      const query = this.normalizedQuery;

      if (!query) {
        return [...this.pageSectionResults, ...this.primaryCommands];
      }

      if (query.startsWith('/')) {
        return this.commandResults.filter((command) => command.searchableText.includes(query));
      }

      const commands = this.commandResults.filter((command) =>
        command.searchableText.includes(query),
      );
      const pageSections = this.pageSectionResults.filter((section) =>
        section.searchableText.includes(query),
      );

      return [...pageSections, ...commands, ...this.apiSearchResults];
    },

    canLoadMoreResults() {
      return this.hasMoreSearchResults && !this.isSearching && !this.isLoadingMoreResults;
    },

    needsMoreSearchCharacters() {
      const query = this.normalizedQuery;

      return Boolean(query && !query.startsWith('/') && query.length <= 3);
    },

    dataViewItems() {
      const results = this.visibleResults.map((result) => ({
        ...result,
        isLoading: false,
      }));

      if (!this.isSearching && !this.isLoadingMoreResults) {
        return results;
      }

      const skeletonCount = results.length > 0 ? 2 : 3;
      const skeletons = this.searchSkeletonRows.slice(0, skeletonCount).map((row, index) => ({
        id: `search-loading-${index}`,
        isLoading: true,
        ...row,
      }));

      return [...results, ...skeletons];
    },
  },

  watch: {
    show(value) {
      if (!value) {
        this.resetSearchState();
        return;
      }

      this.$nextTick(() => {
        this.focusCommandInput();
      });
    },

    query() {
      this.queueSearch();
    },
  },

  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },

  methods: {
    resetSearchState() {
      this.query = '';
      this.apiSearchResults = [];
      this.currentSearchPage = 1;
      this.hasMoreSearchResults = false;
      this.isSearching = false;
      this.isLoadingMoreResults = false;
      this.searchError = null;
      this.failedSearch = null;
      this.latestSearchRequestId += 1;

      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },

    close() {
      this.resetSearchState();
      this.$emit('close');
    },

    focusCommandInput() {
      const input = this.$refs.commandInput?.$el || this.$refs.commandInput;
      input?.focus?.();
    },

    clearSearch() {
      this.query = '';
      this.$nextTick(() => this.focusCommandInput());
    },

    selectFirstResult() {
      const firstAvailableResult = this.visibleResults.find((result) => !result.disabled);

      if (firstAvailableResult) {
        this.selectResult(firstAvailableResult);
      }
    },

    selectResult(result) {
      if (result.disabled) {
        return;
      }

      if (result.isPageSection) {
        this.$emit('jump-page-section', result.section);
        return;
      }

      if (result.action === 'create-estimate') {
        this.$emit('create-estimate');
        this.query = '';
        return;
      }

      if (result.href) {
        this.close();
        router.visit(result.href);
        return;
      }

      this.close();
    },

    queueSearch() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const query = this.normalizedQuery;
      this.searchError = null;
      this.failedSearch = null;

      if (!query || query.startsWith('/') || query.length <= 3) {
        this.latestSearchRequestId += 1;
        this.apiSearchResults = [];
        this.currentSearchPage = 1;
        this.hasMoreSearchResults = false;
        this.isSearching = false;
        this.isLoadingMoreResults = false;
        return;
      }

      this.latestSearchRequestId += 1;
      this.apiSearchResults = [];
      this.currentSearchPage = 1;
      this.hasMoreSearchResults = false;
      this.isSearching = true;
      this.isLoadingMoreResults = false;

      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null;
        this.fetchSearchResults({ page: 1, append: false });
      }, 250);
    },

    normalizeVehicleResults(foundVehicles) {
      if (Array.isArray(foundVehicles)) {
        return foundVehicles;
      }

      if (foundVehicles && typeof foundVehicles === 'object') {
        return Object.values(foundVehicles);
      }

      return [];
    },

    mergeSearchResults(results) {
      return results.filter(
        (result, index, allResults) =>
          result?.href && allResults.findIndex((candidate) => candidate.id === result.id) === index,
      );
    },

    mapCustomerResult(customer) {
      const customerId = customer?.user_id || customer?.id;
      const title =
        customer?.full_name ||
        customer?.customer_name ||
        [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
        'Customer';
      const phone =
        customer?.cell_number ||
        customer?.phone_number ||
        customer?.phone ||
        customer?.phones?.[0]?.phone_number ||
        '';
      const email = customer?.email || '';
      const subtitle = [phone, email].filter(Boolean).join(', ') || 'Customer';

      return {
        id: 'customer-' + customerId,
        type: 'Customer',
        title,
        subtitle,
        icon: 'fa-solid fa-user',
        action: 'navigate',
        href: customerId
          ? route('shop-management.customers.show', {
              shop_id: customer.shop_id || this.masterConfig.shop_id,
              customer: customerId,
            })
          : null,
      };
    },

    mapVehicleResult(vehicle) {
      const vehicleId = vehicle?.vehicle_id;
      const title =
        vehicle.full_vehicle ||
        vehicleLabel(vehicle) ||
        'Customer';

      return {
        id: 'vehicle-' + vehicleId + 'user-' + vehicle.user_id,
        type: 'Vehicle',
        title,
        subtitle: '',
        icon: 'fa-solid fa-car-side',
        action: 'navigate',
        href: vehicleId
          ? route('shop-management.customers.show', {
              shop_id: vehicle.shop_id || this.masterConfig.shop_id,
              customer: vehicle.user_id,
            })
          : null,
      };
    },

    async fetchSearchResults({ page = 1, append = false } = {}) {
      const query = this.normalizedQuery;
      if (!query || query.startsWith('/') || query.length <= 3) {
        this.apiSearchResults = [];
        this.currentSearchPage = 1;
        this.hasMoreSearchResults = false;
        this.isSearching = false;
        this.isLoadingMoreResults = false;
        return;
      }

      const requestId = ++this.latestSearchRequestId;
      this.searchError = null;
      this.failedSearch = null;

      if (append) {
        this.isLoadingMoreResults = true;
      } else {
        this.isSearching = true;
        this.isLoadingMoreResults = false;
      }

      try {
        const response = await axios.get(
          route('customer.search.customers', {
            shop_id: this.masterConfig.shop_id,
            search: query,
            page,
            per_page: this.searchResultsPerPage,
            v4: true,
          }),
        );

        if (requestId !== this.latestSearchRequestId) {
          return;
        }

        const customers = Array.isArray(response?.data?.customers)
          ? response.data.customers
          : Array.isArray(response?.data?.customers?.data)
            ? response.data.customers.data
            : [];

        const customerPaginator = response?.data?.customers;
        const vehicles =
          page === 1 ? this.normalizeVehicleResults(response?.data?.foundVehicles) : [];

        const nextResults = [
          ...customers.map((customer) => this.mapCustomerResult(customer)),
          ...vehicles.map((vehicle) => this.mapVehicleResult(vehicle)),
        ];

        this.apiSearchResults = this.mergeSearchResults(
          append ? [...this.apiSearchResults, ...nextResults] : nextResults,
        );
        this.currentSearchPage = page;
        this.hasMoreSearchResults = Boolean(
          customerPaginator?.next_page_url ||
            (Number.isInteger(customerPaginator?.current_page) &&
              Number.isInteger(customerPaginator?.last_page) &&
              customerPaginator.current_page < customerPaginator.last_page),
        );
      } catch (error) {
        if (requestId !== this.latestSearchRequestId) {
          return;
        }

        if (!append) {
          this.apiSearchResults = [];
        }

        this.searchError = append
          ? 'More results could not be loaded. Your current results are still available.'
          : 'Search results could not be loaded. Check your connection and try again.';
        this.failedSearch = { page, append };
        this.hasMoreSearchResults = append;
      } finally {
        if (requestId === this.latestSearchRequestId) {
          this.isSearching = false;
          this.isLoadingMoreResults = false;
        }
      }
    },

    loadMoreResults() {
      if (!this.canLoadMoreResults) {
        return;
      }

      this.fetchSearchResults({
        page: this.currentSearchPage + 1,
        append: true,
      });
    },

    retryFailedSearch() {
      if (!this.failedSearch) {
        return;
      }

      this.fetchSearchResults({ ...this.failedSearch });
    },
  },
};
</script>

<template>
  <Dialog
    id="shop-management-command-palette"
    :visible="show"
    modal
    dismissableMask
    :draggable="false"
    :closable="false"
    class="bayhive-dialog shop-management-command-dialog"
    :style="{
      width:
        'min(var(--p-bayhive-layout-command-width), calc(100vw - (var(--p-bayhive-layout-command-viewport-inset) * 2)))',
    }"
    :contentStyle="{
      minHeight:
        'min(var(--p-bayhive-layout-command-content-min-height), calc(100vh - var(--p-bayhive-layout-command-viewport-vertical-reserve)))',
    }"
    @update:visible="(value) => !value && close()"
  >
    <template #header="{ class: titleClass, headerId }">
      <div class="shop-management-command-header">
        <span
          :id="headerId"
          :class="titleClass"
          class="shop-management-command-header-title"
        >Search, jump to, or ask</span>
        <small class="shop-management-command-header-meta">In {{ scopeLabel }}</small>
      </div>
    </template>

    <div class="shop-management-command-body">
      <DataView
        :value="dataViewItems"
        dataKey="id"
        layout="list"
        aria-label="BayHive page sections, commands, and customer results"
        class="shop-management-command-results"
      >
        <template #header>
          <div class="shop-management-command-search">
            <IconField>
              <InputIcon>
                <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
              </InputIcon>

              <InputText
                ref="commandInput"
                v-model="query"
                type="text"
                inputmode="search"
                fluid
                autofocus
                placeholder="Search, jump to, or type /hivemind"
                aria-label="Search BayHive commands and customers"
                @keydown.enter.prevent="selectFirstResult"
              />

              <Button
                v-if="query"
                type="button"
                variant="text"
                severity="primary"
                size="small"
                iconOnly
                class="shop-management-command-clear"
                aria-label="Clear search"
                @click="clearSearch"
              >
                <i class="fa-solid fa-xmark" aria-hidden="true" />
              </Button>
            </IconField>
          </div>

          <div class="shop-management-command-section-label" aria-live="polite">
            {{ resultsLabel }}
          </div>
        </template>

        <template #list="{ items }">
          <div
            class="shop-management-command-list"
            :aria-busy="String(isSearching || isLoadingMoreResults)"
          >
            <template v-for="item in items" :key="item.id">
              <Button
                v-if="item.isLoading"
                type="button"
                severity="secondary"
                variant="text"
                size="large"
                disabled
                class="shop-management-command-result shop-management-command-skeleton-row"
                aria-hidden="true"
              >
                <span class="shop-management-command-option">
                <span class="shop-management-command-option-icon"><Skeleton shape="circle" size="1em" aria-hidden="true" /></span>
                <span class="shop-management-command-option-copy" aria-hidden="true">
                  <Skeleton
                    :width="item.titleWidth"
                    height="var(--p-bayhive-typography-body-small-font-size)"
                    borderRadius="var(--p-border-radius-sm)"
                  />
                  <Skeleton
                    :width="item.subtitleWidth"
                    height="var(--p-bayhive-typography-body-caption-font-size)"
                    borderRadius="var(--p-border-radius-sm)"
                  />
                </span>
                </span>
              </Button>

              <Button
                v-else
                type="button"
                severity="secondary"
                variant="text"
                size="large"
                class="shop-management-command-result"
                :disabled="item.disabled"
                @click="selectResult(item)"
              >
                <span class="shop-management-command-option">
                  <span class="shop-management-command-option-icon" aria-hidden="true">
                    <i :class="item.icon" aria-hidden="true" />
                  </span>
                  <span class="shop-management-command-option-copy">
                    <span class="shop-management-command-option-row">
                      <span class="shop-management-command-option-title">{{ item.title }}</span>
                      <small v-if="item.slash" class="shop-management-command-option-alias">{{
                        item.slash
                      }}</small>
                    </span>
                    <small class="shop-management-command-option-subtitle">
                      {{ item.type }}<span v-if="item.subtitle"> · {{ item.subtitle }}</span>
                    </small>
                  </span>
                </span>
              </Button>
            </template>
          </div>
        </template>

        <template #empty>
          <span v-if="searchError" class="shop-management-command-empty">
            <strong>Results unavailable</strong>
            <small>Retry the search below.</small>
          </span>

          <span v-else-if="needsMoreSearchCharacters" class="shop-management-command-empty">
            <strong>Keep typing to search customers</strong>
            <small>Enter at least 4 characters.</small>
          </span>

          <span v-else class="shop-management-command-empty">
            <strong>No matching results</strong>
            <small>Try a customer name, phone number, email, or command.</small>
          </span>
        </template>
      </DataView>

      <Message v-if="searchError" severity="error" :closable="false">
        <span class="shop-management-command-error">
          <span>{{ searchError }}</span>
          <Button type="button" size="small" variant="text" @click="retryFailedSearch">
            Retry
          </Button>
        </span>
      </Message>
    </div>

    <template #footer>
      <div class="shop-management-command-footer">
        <small class="shop-management-command-footer-copy">
          Use Tab to move through results and Enter to open.
        </small>

        <div class="shop-management-command-footer-actions">
          <Button type="button" variant="text" @click="close">Close</Button>
          <Button
            v-if="hasMoreSearchResults"
            type="button"
            :disabled="!canLoadMoreResults"
            @click="loadMoreResults"
          >
            <i v-if="isLoadingMoreResults" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            <span>{{
              isLoadingMoreResults ? 'Loading more results...' : 'Load more results'
            }}</span>
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.shop-management-command-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--p-bayhive-spacing-xs);
  width: 100%;
  min-width: 0;
}

.shop-management-command-header-title {
  font-weight: var(--p-bayhive-typography-label-font-weight);
}

.shop-management-command-header-meta {
  color: var(--p-text-muted-color);
}

.shop-management-command-body {
  display: grid;
  gap: var(--p-bayhive-spacing-lg);
}

.shop-management-command-section-label {
  font-size: var(--p-bayhive-typography-overline-font-size);
  font-weight: var(--p-bayhive-typography-overline-font-weight);
  letter-spacing: var(--p-bayhive-typography-overline-letter-spacing);
  line-height: var(--p-bayhive-typography-overline-line-height);
  text-transform: uppercase;
  color: var(--p-text-muted-color);
}

.shop-management-command-search {
  width: 100%;
}

.shop-management-command-search :deep(.p-iconfield),
.shop-management-command-search :deep(.p-inputtext) {
  width: 100%;
}

.shop-management-command-results {
  width: 100%;
}

.shop-management-command-results :deep(.p-dataview-content) {
  height: clamp(
    var(--p-bayhive-layout-command-results-min-height),
    40vh,
    var(--p-bayhive-layout-command-results-max-height)
  );
  overflow-y: auto;
}

.shop-management-command-list {
  display: grid;
  gap: var(--p-bayhive-spacing-sm);
  width: 100%;
}

.shop-management-command-result {
  justify-content: flex-start;
  width: 100%;
  text-align: start;
}

.shop-management-command-skeleton-row {
  opacity: 1;
}

.shop-management-command-skeleton-copy {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
  gap: var(--p-bayhive-spacing-sm);
}

.shop-management-command-empty {
  display: grid;
  gap: var(--p-bayhive-spacing-xs);
}

.shop-management-command-empty small {
  color: var(--p-text-muted-color);
}

.shop-management-command-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--p-bayhive-spacing-md);
  width: 100%;
}

.shop-management-command-option {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: var(--p-bayhive-spacing-md);
}

.shop-management-command-option-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: var(--p-bayhive-layout-command-option-icon-width);
  color: var(--p-text-muted-color);
}

.shop-management-command-option-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--p-bayhive-spacing-xs);
}

.shop-management-command-option-row {
  display: flex;
  min-width: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--p-bayhive-spacing-md);
}

.shop-management-command-option-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--p-bayhive-typography-label-font-weight);
}

.shop-management-command-option-alias {
  flex-shrink: 0;
  white-space: nowrap;
  font-family: var(--p-bayhive-typography-monospace-font-family);
  color: var(--p-text-muted-color);
}

.shop-management-command-option-subtitle {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--p-text-muted-color);
}

.shop-management-command-footer {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--p-bayhive-spacing-sm);
}

.shop-management-command-footer-copy {
  color: var(--p-text-muted-color);
}

.shop-management-command-footer-actions {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-sm);
}

@media (max-width: 640px) {
  .shop-management-command-footer {
    align-items: stretch;
  }

  .shop-management-command-footer-actions {
    justify-content: space-between;
  }
}
</style>
